/* eslint-disable react-refresh/only-export-components */
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
import { AuthGuard } from '@/components/auth/AuthGuard';
import { GuestGuard } from '@/components/auth/GuestGuard';
import { PermissionGuard } from '@/components/auth/PermissionGuard';
import { PortalLayout } from '@/layouts/PortalLayout';
import { LoadingDots } from '@/components/common/LoadingDots';
import { DefaultRedirect } from '@/components/auth/DefaultRedirect';
import NotFoundPage from '@/pages/errors/NotFoundPage';

// Lazy-loaded pages
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const TwoFactorPage = lazy(() => import('@/pages/auth/TwoFactorPage'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));
const AuthCallbackPage = lazy(() => import('@/pages/auth/AuthCallbackPage'));
const VerifyEmailPage = lazy(() => import('@/pages/auth/VerifyEmailPage'));
const AcceptInvitePage = lazy(() => import('@/pages/auth/AcceptInvitePage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));
const OnboardingPage = lazy(() => import('@/pages/onboarding/OnboardingPage'));
const SetupPage = lazy(() => import('@/pages/setup/SetupPage'));
const DataSourcesPage = lazy(() => import('@/pages/datasources/DataSourcesPage'));
const CreateDataSourcePage = lazy(() => import('@/pages/datasources/CreateDataSourcePage'));
const DataSourceDetailsPage = lazy(() => import('@/pages/datasources/DataSourceDetailsPage'));
const EditDataSourcePage = lazy(() => import('@/pages/datasources/EditDataSourcePage'));
const CatalogPage = lazy(() => import('@/pages/catalog/CatalogPage'));
const NotebooksPage = lazy(() => import('@/pages/notebooks/NotebooksPage'));
const NotebookEditorPage = lazy(() => import('@/pages/notebooks/NotebookEditorPage'));
const DataExplorerPage = lazy(() => import('@/pages/queries/DataExplorerPage'));
const DashboardsPage = lazy(() => import('@/pages/bi/DashboardsPage'));
const ViewDashboardPage = lazy(() => import('@/pages/bi/ViewDashboardPage'));
const JobsPage = lazy(() => import('@/pages/jobs/JobsPage'));
const AccountSettingsPage = lazy(() => import('@/pages/settings/AccountSettingsPage'));
const AppSettingsPage = lazy(() => import('@/pages/settings/AppSettingsPage'));
const EnvironmentsPage = lazy(() => import('@/pages/settings/EnvironmentsPage'));
const APIKeysPage = lazy(() => import('@/pages/settings/APIKeysPage'));
const PipelinesPage = lazy(() => import('@/pages/pipelines/PipelinesPage'));
const PipelineRunsPage = lazy(() => import('@/pages/pipelines/PipelineRunsPage'));
const PipelineBuilderPage = lazy(() => import('@/pages/pipelines/builder/PipelineBuilderPage'));
const TransformsListPage = lazy(() => import('@/pages/transforms/TransformsListPage'));
const TransformBuilderPage = lazy(() => import('@/pages/transforms/TransformBuilderPage'));
const AgentsPage = lazy(() => import('@/pages/coordinators/AgentsPage'));
const AgentDetailsPage = lazy(() => import('@/pages/coordinators/AgentDetailsPage'));
const OrganizationSettingsPage = lazy(() => import('@/pages/settings/OrganizationSettingsPage'));
const NotificationChannelsPage = lazy(() => import('@/pages/settings/NotificationChannelsPage'));
const AdminPage = lazy(() => import('@/pages/settings/AdminPage'));
const SecurityGroupsPage = lazy(() => import('@/pages/settings/SecurityGroupsPage'));
const SsoSettingsPage = lazy(() => import('@/pages/settings/SsoSettingsPage'));
const PoliciesPage = lazy(() => import('@/pages/policies/PoliciesPage'));
const AiChatPage = lazy(() => import('@/pages/ai/AiChatPage'));
const SeedDataPage = lazy(() => import('@/pages/seed-data/SeedDataPage'));
const PermissionEditorPage = lazy(() => import('@/pages/advanced/PermissionEditorPage'));

// CLI setup
const CliSetupPage = lazy(() => import('@/pages/cli/CliSetupPage'));

// Generic product pages
const ProductLandingPage = lazy(() => import('@/pages/products/ProductLandingPage'));
const DevAnalyticsLandingPage = lazy(() => import('@/pages/products/DevAnalyticsLandingPage'));
const ProductOnboardingPage = lazy(() => import('@/pages/products/ProductOnboardingPage'));
const ProductProvidersPage = lazy(() => import('@/pages/products/ProductProvidersPage'));
const ProductDataTablesPage = lazy(() => import('@/pages/products/ProductDataTablesPage'));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-base)]">
      <LoadingDots size="large" />
    </div>
  );
}

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

function Placeholder({ title }: { title: string }) {
  return (
    <div className="flex items-center justify-center" style={{ minHeight: 300 }}>
      <div className="text-center">
        <h2
          style={{
            fontSize: 'var(--s-text-lg)',
            fontWeight: 600,
            color: 'var(--text-primary)',
            marginBottom: '0.5rem',
          }}
        >
          {title}
        </h2>
        <p style={{ fontSize: 'var(--s-text-base)', color: 'var(--text-muted)' }}>Coming soon</p>
      </div>
    </div>
  );
}

export const router = createBrowserRouter([
  // Product-specific landing pages (public, no guard)
  {
    path: '/products/devanalytics',
    element: (
      <SuspenseWrapper>
        <DevAnalyticsLandingPage />
      </SuspenseWrapper>
    ),
  },

  // Generic product landing page (public, no guard)
  {
    path: '/products/:slug',
    element: (
      <SuspenseWrapper>
        <ProductLandingPage />
      </SuspenseWrapper>
    ),
  },

  // Backward-compat redirects for old FinOps routes
  {
    path: '/finops/onboarding',
    element: <Navigate to="/p/finops/onboarding" replace />,
  },
  {
    path: '/finops/providers',
    element: <Navigate to="/p/finops/providers" replace />,
  },

  // Public routes (redirect authenticated users away)
  {
    element: <GuestGuard />,
    children: [
      {
        path: '/login',
        element: (
          <SuspenseWrapper>
            <LoginPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: '/register',
        element: (
          <SuspenseWrapper>
            <RegisterPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },

  // 2FA verification (no guard — user has partial session but needs 2FA)
  {
    path: '/login/verify-2fa',
    element: (
      <SuspenseWrapper>
        <TwoFactorPage />
      </SuspenseWrapper>
    ),
  },

  // Accept invitation (no guard — user clicks link from email)
  {
    path: '/accept-invite',
    element: (
      <SuspenseWrapper>
        <AcceptInvitePage />
      </SuspenseWrapper>
    ),
  },

  // Forgot password (no guard — unauthenticated flow)
  {
    path: '/forgot-password',
    element: (
      <SuspenseWrapper>
        <ForgotPasswordPage />
      </SuspenseWrapper>
    ),
  },

  // Reset password (no guard — user clicks link from email)
  {
    path: '/reset-password',
    element: (
      <SuspenseWrapper>
        <ResetPasswordPage />
      </SuspenseWrapper>
    ),
  },

  // Email verification callback (no guard — user clicks link from email)
  {
    path: '/verify-email/confirm',
    element: (
      <SuspenseWrapper>
        <VerifyEmailPage />
      </SuspenseWrapper>
    ),
  },

  // OAuth callback route (no guard) — Better Auth redirects here after social login
  {
    path: '/auth/callback',
    element: (
      <SuspenseWrapper>
        <AuthCallbackPage />
      </SuspenseWrapper>
    ),
  },

  // Onboarding + email verification (protected, but no sidebar layout)
  {
    element: <AuthGuard />,
    children: [
      {
        path: '/verify-email',
        element: (
          <SuspenseWrapper>
            <VerifyEmailPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: '/onboarding',
        element: (
          <SuspenseWrapper>
            <OnboardingPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: '/p/:slug/onboarding',
        element: (
          <SuspenseWrapper>
            <ProductOnboardingPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },

  // Portal routes (protected + sidebar layout)
  {
    element: <AuthGuard />,
    children: [
      {
        element: <PortalLayout />,
        children: [
          { path: '/', element: <DefaultRedirect /> },

          // Get Started
          {
            element: <PermissionGuard featureKey="get_started.setup_guide" />,
            children: [
              {
                path: '/setup',
                element: (
                  <SuspenseWrapper>
                    <SetupPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },

          // Ingestion — Data Sources
          {
            element: <PermissionGuard featureKey="ingestion.data_sources" />,
            children: [
              {
                path: '/data-sources',
                element: (
                  <SuspenseWrapper>
                    <DataSourcesPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: '/data-sources/new',
                element: (
                  <SuspenseWrapper>
                    <CreateDataSourcePage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: '/data-sources/:id',
                element: (
                  <SuspenseWrapper>
                    <DataSourceDetailsPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: '/data-sources/:id/edit',
                element: (
                  <SuspenseWrapper>
                    <EditDataSourcePage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },

          // Ingestion — Pipelines
          {
            element: <PermissionGuard featureKey="ingestion.pipelines" />,
            children: [
              {
                path: '/pipelines',
                element: (
                  <SuspenseWrapper>
                    <PipelinesPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: '/pipelines/new',
                element: (
                  <SuspenseWrapper>
                    <PipelineBuilderPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: '/pipelines/:id/runs',
                element: (
                  <SuspenseWrapper>
                    <PipelineRunsPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: '/pipelines/:id/edit',
                element: (
                  <SuspenseWrapper>
                    <PipelineBuilderPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },

          // Ingestion — Transforms
          {
            element: <PermissionGuard featureKey="ingestion.transforms" />,
            children: [
              {
                path: '/transforms',
                element: (
                  <SuspenseWrapper>
                    <TransformsListPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: '/transforms/new',
                element: (
                  <SuspenseWrapper>
                    <TransformBuilderPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: '/transforms/:id',
                element: (
                  <SuspenseWrapper>
                    <TransformBuilderPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },

          // Data Lake — Catalog
          {
            element: <PermissionGuard featureKey="data_lake.catalog" />,
            children: [
              {
                path: '/catalog',
                element: (
                  <SuspenseWrapper>
                    <CatalogPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },

          // Data Lake — Explorer
          {
            element: <PermissionGuard featureKey="data_lake.explorer" />,
            children: [
              {
                path: '/queries',
                element: (
                  <SuspenseWrapper>
                    <DataExplorerPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },

          // Data Lake — Notebooks
          {
            element: <PermissionGuard featureKey="data_lake.notebooks" />,
            children: [
              {
                path: '/notebooks',
                element: (
                  <SuspenseWrapper>
                    <NotebooksPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: '/notebooks/:id',
                element: (
                  <SuspenseWrapper>
                    <NotebookEditorPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },

          // Data Lake — Seed Data
          {
            element: <PermissionGuard featureKey="data_lake.seed_data" />,
            children: [
              {
                path: '/seed-data',
                element: (
                  <SuspenseWrapper>
                    <SeedDataPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },

          // Analytics — Dashboards
          {
            element: <PermissionGuard featureKey="analytics.dashboards" />,
            children: [
              {
                path: '/cli/setup',
                element: (
                  <SuspenseWrapper>
                    <CliSetupPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: '/bi',
                element: (
                  <SuspenseWrapper>
                    <DashboardsPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: '/bi/:id',
                element: (
                  <SuspenseWrapper>
                    <ViewDashboardPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },

          // Scout
          {
            element: <PermissionGuard featureKey="scout.scout" />,
            children: [
              {
                path: '/ai',
                element: (
                  <SuspenseWrapper>
                    <AiChatPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },

          // Infrastructure — Agents
          {
            element: <PermissionGuard featureKey="infrastructure.agents" />,
            children: [
              {
                path: '/coordinators',
                element: (
                  <SuspenseWrapper>
                    <AgentsPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: '/coordinators/:id',
                element: (
                  <SuspenseWrapper>
                    <AgentDetailsPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },

          // Infrastructure — Jobs
          {
            element: <PermissionGuard featureKey="infrastructure.jobs" />,
            children: [
              {
                path: '/jobs',
                element: (
                  <SuspenseWrapper>
                    <JobsPage />
                  </SuspenseWrapper>
                ),
              },
              { path: '/jobs/:id', element: <Placeholder title="Job Details" /> },
            ],
          },

          // Product pages (generic — no feature gate)
          {
            path: '/p/:slug/providers',
            element: (
              <SuspenseWrapper>
                <ProductProvidersPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: '/p/:slug/data',
            element: (
              <SuspenseWrapper>
                <ProductDataTablesPage />
              </SuspenseWrapper>
            ),
          },

          // Settings — Account (no permission gate — all authenticated users)
          {
            path: '/settings/account',
            element: (
              <SuspenseWrapper>
                <AccountSettingsPage />
              </SuspenseWrapper>
            ),
          },

          // Settings — App Settings
          {
            element: <PermissionGuard featureKey="settings.app_settings" />,
            children: [
              {
                path: '/settings',
                element: (
                  <SuspenseWrapper>
                    <AppSettingsPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },

          // Settings — Organization
          {
            element: <PermissionGuard featureKey="settings.organization" />,
            children: [
              {
                path: '/settings/organization',
                element: (
                  <SuspenseWrapper>
                    <OrganizationSettingsPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },

          // Settings — Environments
          {
            element: <PermissionGuard featureKey="settings.environments" />,
            children: [
              {
                path: '/settings/environments',
                element: (
                  <SuspenseWrapper>
                    <EnvironmentsPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },

          // Settings — API Keys
          {
            element: <PermissionGuard featureKey="settings.api_keys" />,
            children: [
              {
                path: '/settings/api-keys',
                element: (
                  <SuspenseWrapper>
                    <APIKeysPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },

          // Settings — Notifications
          {
            element: <PermissionGuard featureKey="settings.notifications" />,
            children: [
              {
                path: '/settings/notifications',
                element: (
                  <SuspenseWrapper>
                    <NotificationChannelsPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },

          // Settings — Security Groups
          {
            element: <PermissionGuard featureKey="settings.security_groups" />,
            children: [
              {
                path: '/settings/security-groups',
                element: (
                  <SuspenseWrapper>
                    <SecurityGroupsPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },

          // Settings — SSO
          {
            element: <PermissionGuard featureKey="settings.sso" />,
            children: [
              {
                path: '/settings/sso',
                element: (
                  <SuspenseWrapper>
                    <SsoSettingsPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },

          // Data Lake — Policies
          {
            element: <PermissionGuard featureKey="data_lake.policies" />,
            children: [
              {
                path: '/settings/policies',
                element: (
                  <SuspenseWrapper>
                    <PoliciesPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },

          // Advanced — Admin Panel
          {
            element: <PermissionGuard featureKey="advanced.admin_panel" />,
            children: [
              {
                path: '/settings/admin',
                element: (
                  <SuspenseWrapper>
                    <AdminPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },

          // Advanced — Permission Editor (staff only)
          {
            element: <PermissionGuard staffOnly />,
            children: [
              {
                path: '/admin/permissions',
                element: (
                  <SuspenseWrapper>
                    <PermissionEditorPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },

          // 404 — catch-all for authenticated portal routes
          { id: 'not-found', path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },

  // Root-level catch-all: redirect unknown paths to / (AuthGuard handles auth)
  { path: '*', element: <Navigate to="/" replace /> },
]);
